<template>
  <li ref="listItemRef" :data-draggable="isDraggable" class="tw-relative">
    <slot />

    <c-drop-indicator
      v-if="
        isDraggable && state.type === 'is-dragging-over' && state.closestEdge
      "
      :edge="state.closestEdge"
      :gap
    />
  </li>
</template>

<script setup>
import { onMounted, onUnmounted, ref, useTemplateRef } from "vue";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/utils/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter";
import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge";

// Props
const props = defineProps({
  listOrder: {
    type: Number,
    required: false,
  },
  dataType: {
    type: String,
    required: false,
  },
  dataId: {
    type: [String, Number],
    required: false,
  },
  gap: {
    type: String,
    default: () => ".5rem",
  },
  isDraggable: {
    type: Boolean,
    default: () => false,
  },
  disable: {
    type: Boolean,
    default: () => false,
  },
});

// Template refs
const listItemRef = useTemplateRef("listItemRef");

// State
const state = ref({ type: "idle" });

// Cleanup
let cleanup = () => {};

const getData = () => ({
  dataType: props.dataType,
  dataId: props.dataId,
});

// Check if dropping here would be a no-op (source is the adjacent sibling on that edge)
const isAdjacentNoOp = (sourceElement, closestEdge) => {
  if (!listItemRef.value) {
    return false;
  }

  const prev = listItemRef.value.previousElementSibling;
  const next = listItemRef.value.nextElementSibling;

  if (closestEdge === "top" && prev === sourceElement) {
    return true;
  }

  if (closestEdge === "bottom" && next === sourceElement) {
    return true;
  }

  return false;
};

onMounted(() => {
  if (props.disable || !props.isDraggable) {
    return;
  }

  cleanup = combine(
    draggable({
      element: listItemRef.value,
      getInitialData: () => getData(),
      onDragStart: () => {
        state.value = { type: "is-dragging" };
      },
      onDrop: () => {
        state.value = { type: "idle" };
      },
      canDrag: () => props.isDraggable && !props.disable,
    }),
    dropTargetForElements({
      element: listItemRef.value,
      canDrop: ({ source }) => {
        if (source.element === listItemRef.value) {
          return false;
        }

        return source.data?.dataType === props.dataType;
      },
      getData: ({ input }) =>
        attachClosestEdge(getData(), {
          element: listItemRef.value,
          input,
          allowedEdges: ["top", "bottom"],
        }),
      getIsSticky: () => true,
      onDragEnter: ({ self, source }) => {
        const closestEdge = extractClosestEdge(self.data);

        if (isAdjacentNoOp(source.element, closestEdge)) {
          state.value = { type: "idle" };
          return;
        }

        state.value = { type: "is-dragging-over", closestEdge };
      },
      onDrag({ self, source }) {
        const closestEdge = extractClosestEdge(self.data);

        if (isAdjacentNoOp(source.element, closestEdge)) {
          if (state.value.type !== "idle") {
            state.value = { type: "idle" };
          }
          return;
        }

        if (
          state.value.type !== "is-dragging-over" ||
          state.value.closestEdge !== closestEdge
        ) {
          state.value = { type: "is-dragging-over", closestEdge };
        }
      },
      onDragLeave: () => {
        state.value = { type: "idle" };
      },
      onDrop: () => {
        state.value = { type: "idle" };
      },
    }),
  );
});

onUnmounted(() => {
  cleanup();
});
</script>
